import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PolIcon from '../../../../assets/icons/main/polCoin.svg';
import DuoIcon from '../../../../assets/icons/dashboard/programs/programCard/duoIcon.svg';
import QuattroIcon from '../../../../assets/icons/dashboard/programs/programCard/quattroIcon.svg';

import { useUpgradeLvl } from '../../../../helpers/hooks/useUpgradeLvl';
import { PROGRAM_NAMES, PROGRAM_PRICES_WEI, PROGRAM_FEE_WEI } from '../../../../helpers/constants';
import { Button } from '../../../../components/Button';
import { useLocation } from 'react-router-dom';
import { useCallTransaction } from '../../../../helpers/hooks/useCallTransaction';
import { usePreviewMode } from '../../../../helpers/hooks/useCheckPreviewMode';
import { round } from '../../../../helpers/numbers';
import { useTranslation } from 'react-i18next';
import EnergyIcon from '../../../../assets/icons/passive/energyWhite.svg';
import { BuyWithOptionals } from '../../../../components/Modals/BuyWithOptionals';

export const ProgramCard = ({
  type,
  profit = 0,
  maxActiveLevel = 0,
  lastIncomeLevel = 0,
  updateInfo,
  isLevelsReady = true,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const maxLevel = 15;
  const percentProgress = ((maxActiveLevel / maxLevel) * 100).toFixed(0) || 0;
  const { upgradeLvl } = useUpgradeLvl();
  const { onCallTransaction, transactionInfo, resetTransactionInfo } = useCallTransaction();
  const { previewUserAddress } = usePreviewMode();
  const [isBuyModalOpened, setIsBuyModalOpened] = useState(false);

  useEffect(() => {
    if (transactionInfo.isSuccess) {
      updateInfo();
      resetTransactionInfo();
    }
  }, [transactionInfo]);

  const isAllowToBuyWithWeekly = useMemo(() => {
    return Number(lastIncomeLevel) === Number(maxActiveLevel) && maxActiveLevel > 0;
  }, [lastIncomeLevel, maxActiveLevel]);

  const upgradeClick = async (levelsX2, levelsX4, activateIncome = null) => {
    try {
      const shouldActivateIncome = activateIncome !== null ? activateIncome : isAllowToBuyWithWeekly;
      const result = await upgradeLvl(levelsX2, levelsX4, shouldActivateIncome);

      if (result) {
        onCallTransaction(result);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleBuyClick = () => {
    setIsBuyModalOpened(true);
  };

  const handleBuyWithWeekly = () => {
    setIsBuyModalOpened(false);
    upgradeClick(levelsX2, levelsX4, true);
  };

  const handleBuyOnlyLevel = () => {
    setIsBuyModalOpened(false);
    upgradeClick(levelsX2, levelsX4, false);
  };

  const levelsX2 = type === PROGRAM_NAMES.X2 ? [maxActiveLevel + 1] : [];
  const levelsX4 = type === PROGRAM_NAMES.X4 ? [maxActiveLevel + 1] : [];

  const totalPrice =
    (Number(PROGRAM_PRICES_WEI[type][maxActiveLevel]) + Number(PROGRAM_FEE_WEI[type][maxActiveLevel])) / 1e18;

  const totalPriceWithWeekly =
    (Number(PROGRAM_PRICES_WEI[type][maxActiveLevel]) +
      Number(PROGRAM_PRICES_WEI[type][maxActiveLevel]) * 0.2 +
      Number(PROGRAM_FEE_WEI[type][maxActiveLevel])) /
    1e18;

  return (
    <div className="relative flex flex-col space-y-[4.8rem] items-center dashboard-block-styles p-[2rem] lg:w-[89%] lg:shrink-0 lg:space-y-[5.6rem]">
      <Link
        to={previewUserAddress ? `/weeklyReward?previewUser=${previewUserAddress}` : '/weeklyReward'}
        className="flex justify-between w-full "
      >
        <div className="flex items-center space-x-[1.2rem]">
          <img className="w-[2.8rem] h-[2.8rem]" src={type === PROGRAM_NAMES.X2 ? DuoIcon : QuattroIcon} />
          <span className="text-[1.8rem] font-600 text-white lg:text-[1.6rem]">
            {t('dashboard.programs.card.title', { program: type })}
          </span>
        </div>
        <button className="flex items-center justify-center space-x-[1rem] pl-[0.8rem] rounded-[2rem] pr-[1.2rem] h-[2.8rem] bg-[#985AFF] border-[0.1rem] border-solid border-white-200 custom-transition hover:bg-[#8133FF]">
          <img className="w-[1.6rem] h-[1.6rem]" src={EnergyIcon} />
          <span className="text-[1.4rem] font-500 tracking-[-0.28px] text-white">Boost</span>
        </button>
      </Link>
      <div className="flex flex-col space-y-[2.4rem] w-full z-[11]">
        <div className="flex flex-col space-y-[1.6rem] w-full">
          <div className="flex items-center justify-between w-full lg:flex-col lg:items-start lg:space-y-reverse lg:relative ">
            <div className="flex flex-col space-y-[0.8rem] lg:mb-[2.4rem]">
              <span className="text-[1.2rem] tracking-[-0.24px] text-white-500">Total profit</span>
              <div className="flex items-center space-x-[0.8rem]">
                <span className=" text-[2rem] text-white font-600">{round(Number(profit) / 1e18)}</span>
                <img className="w-[2rem] h-[2rem]" src={PolIcon} />
              </div>
            </div>
            <div className="h-[4.7rem] w-[0.1rem] bg-white-100 lg:hidden" />
            <div className="flex flex-col items-start space-y-[0.8rem]">
              <span className="text-[1.2rem] text-white-500 tracking-[-0.24px] text-left">
                {t('dashboard.programs.card.total')} {t('dashboard.programs.card.activity')}
              </span>
              <span className="text-[2rem] text-white font-600">{percentProgress}%</span>
            </div>

            <div className="grid grid-cols-6 grid-rows-2 items-center justify-center shrink-0 lg:absolute lg:bottom-0 lg:right-0">
              {new Array(maxLevel).fill({}).map((item, itemIndex) => {
                const isActive = itemIndex < maxActiveLevel;
                const isFirstAfterActive = itemIndex === maxActiveLevel;
                return (
                  <div className="flex justify-center items-center w-[2.5rem] h-[2.5rem]">
                    <div
                      //сделал бы объектом
                      key={itemIndex}
                      className={`rounded-full ${
                        isActive
                          ? `w-[1.8rem] h-[1.8rem] ${type === PROGRAM_NAMES.X2 ? 'bg-[#51F689]' : 'bg-[#F1A13B]'}`
                          : isFirstAfterActive
                          ? `w-[1.4rem] h-[1.4rem] ${
                              type === PROGRAM_NAMES.X2 ? 'bg-[rgba(81,246,137,0.30)]' : 'bg-[rgba(241,161,59,0.30)]'
                            }`
                          : 'bg-white-100 w-[1.2rem] h-[1.2rem]'
                      }`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex items-center space-x-[0.4rem] w-full">
            <div
              style={{ width: `${percentProgress}%` }}
              className={`h-[0.8rem] ${
                type === PROGRAM_NAMES.X2 ? 'bg-[#51F689]' : 'bg-[#F1A13B]'
              } bg-[#51F689] rounded-[2rem]`}
            />
            <div className="h-[0.8rem] bg-white-100 rounded-[2rem] flex-1" />
          </div>
        </div>
        <div className="flex items-center space-x-[1.2rem] w-full">
          <Link
            to={`/dashboard/${type.toLowerCase()}?${params.toString()}`}
            className="flex items-center justify-center h-[4rem] w-full"
          >
            <Button type="black" className="h-full w-full">
              <span className="text-[1.4rem] font-500 leading-[1.96rem]">{t('dashboard.programs.card.view')}</span>
            </Button>
          </Link>
          {maxActiveLevel !== maxLevel && (
            <Button
              onClick={isAllowToBuyWithWeekly ? handleBuyClick : handleBuyOnlyLevel}
              type={type === PROGRAM_NAMES.X2 ? 'green' : 'orange'}
              className={`h-[4rem] w-full flex-col`}
              loadingType="black"
              loading={transactionInfo.isWaiting}
              disabled={transactionInfo.isWaiting || previewUserAddress || !isLevelsReady}
            >
              <span className="text-[1.3rem] font-500 leading-[1.6rem]">{t('dashboard.programs.card.activate')}</span>
              <div className="flex items-center justify-center space-x-[0.2rem] text-[1.2rem] font-500 leading-[1.5rem]">
                <span>
                  {t('dashboard.programs.card.for')} {totalPrice}{' '}
                </span>
                <img className="w-[1rem] h-[1rem]" src={PolIcon} />
              </div>
            </Button>
          )}
        </div>
      </div>
      <BuyWithOptionals
        isOpened={isBuyModalOpened}
        onClose={() => setIsBuyModalOpened(false)}
        onBuyWithWeekly={handleBuyWithWeekly}
        onBuyOnlyLevel={handleBuyOnlyLevel}
        totalPriceLevel={totalPrice}
        totalPriceWithWeekly={totalPriceWithWeekly}
      />
      <img
        className="absolute bottom-0 z-[0]"
        src={`/images/dashboard/programs/programCard/${type === PROGRAM_NAMES.X2 ? 'duoShadow' : 'quattroShadow'}.webp`}
      />
    </div>
  );
};
